import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router';

// Generic page scroll restoration that works with our MainLayout scroll containers
// - Restores per-route and per-organization
// - Handles both window and internal #main scroll containers (mobile & desktop)
export const usePageScrollRestoration = () => {
  const { pathname } = useLocation();
  const debounceRef = useRef<number | null>(null);

  const getOrgId = () => {
    try {
      return localStorage.getItem('currentOrganizationId') || 'default';
    } catch {
      return 'default';
    }
  };

  const getStorageKey = () => `scroll:${pathname}:${getOrgId()}`;

  const getScrollContainer = (): Window | HTMLElement => {
    const inner = document.querySelector('#main .container.overflow-y-auto') as HTMLElement | null;
    const main = document.querySelector('#main') as HTMLElement | null;

    const isScrollable = (el: HTMLElement | null) => {
      if (!el) return false;
      const style = window.getComputedStyle(el);
      const oy = style.overflowY;
      return (oy === 'auto' || oy === 'scroll') && el.scrollHeight > el.clientHeight;
    };

    if (isScrollable(inner)) return inner;
    if (isScrollable(main)) return main as HTMLElement;
    return window;
  };
  useEffect(() => {
    const container = getScrollContainer();

    // Restore on mount for this route/org
    try {
      const raw = localStorage.getItem(getStorageKey());
      const saved = raw ? parseInt(raw, 10) : 0;
      // Delay to ensure layout stabilized
      requestAnimationFrame(() => {
        if (container instanceof Window) {
          window.scrollTo({ top: saved, left: 0, behavior: 'auto' });
        } else {
          container.scrollTo({ top: saved, left: 0, behavior: 'auto' });
        }
      });
    } catch {}

    const handleScroll = () => {
      const pos = container instanceof Window ? window.scrollY : (container as HTMLElement).scrollTop;
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
      debounceRef.current = window.setTimeout(() => {
        try {
          localStorage.setItem(getStorageKey(), String(Math.max(0, Math.floor(pos))));
        } catch {}
      }, 120);
    };

    // Attach to proper scroller
    if (container instanceof Window) {
      window.addEventListener('scroll', handleScroll, { passive: true });
    } else {
      container.addEventListener('scroll', handleScroll, { passive: true });
    }

    return () => {
      if (container instanceof Window) {
        window.removeEventListener('scroll', handleScroll);
      } else {
        container.removeEventListener('scroll', handleScroll);
      }
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
    };
  }, [pathname]);
};
